/**
 * Every field a template run returned, so the table can show more than the template's own columns.
 *
 *   discoverFields(rows, template.columns)
 *   -> [{ key: "lat", label: "Lat", format: "number" }, { key: "addressParts.city", label: "Address parts: City" }, ...]
 *
 * Walks the rows (the first 300 are plenty), keeps fields that have a value in at least one row, goes one level into
 * nested objects (place.title, buyBox.seller), treats lists as one field, and guesses each field's format from its
 * values. The template's own columns are left out (they are listed separately), and so are internal fields (_x).
 */
import type { TemplateColumn } from "@/services/templates.service";

type Rec = Record<string, unknown>;
type Format = NonNullable<TemplateColumn["format"]>;

const SAMPLE_ROWS = 300;
const MAX_FIELDS = 120;

// Reader bookkeeping, not data: which parts of the page it could read, debug details.
const NOT_DATA = /^(fieldStatus|fieldsStatus|diagnostics|debug|meta|_.*)$/i;

const isEmpty = (v: unknown) => v == null || v === "" || (Array.isArray(v) && v.length === 0);
const isPlainObject = (v: unknown): v is Rec => !!v && typeof v === "object" && !Array.isArray(v);

/** "reviewsCount" -> "Reviews count", "addressParts.city" -> "Address parts: City". */
export function humanize(path: string): string {
  return path
    .split(".")
    .map(part => {
      const words = part
        .replace(/^_+/, "")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .trim()
        .toLowerCase();
      return words.charAt(0).toUpperCase() + words.slice(1);
    })
    .join(": ");
}

function guessFormat(key: string, values: unknown[]): Format {
  const present = values.filter(v => !isEmpty(v));
  if (!present.length) return "text";
  if (present.every(v => typeof v === "number")) return "number";
  if (present.every(v => typeof v === "boolean")) return "boolean";
  if (present.every(v => Array.isArray(v))) return "list";
  if (present.every(v => typeof v === "string")) {
    const s = present as string[];
    if (s.every(x => /^https?:\/\//i.test(x))) return /image|thumbnail|photo|avatar|logo|picture/i.test(key) ? "image" : "url";
    if (s.every(x => /^\d{4}-\d{2}-\d{2}/.test(x))) return "date";
    if (s.some(x => x.length > 120)) return "longtext";
  }
  return "text";
}

export function discoverFields(rows: Rec[], templateColumns: TemplateColumn[]): TemplateColumn[] {
  const taken = new Set(templateColumns.map(c => c.key));
  const seen = new Map<string, unknown[]>();
  const add = (key: string, value: unknown) => {
    if (!seen.has(key)) seen.set(key, []);
    seen.get(key)!.push(value);
  };
  for (const row of rows.slice(0, SAMPLE_ROWS)) {
    for (const [k, v] of Object.entries(row)) {
      if (NOT_DATA.test(k)) continue;
      if (isPlainObject(v)) {
        for (const [k2, v2] of Object.entries(v)) if (!k2.startsWith("_")) add(`${k}.${k2}`, v2);
      } else add(k, v);
    }
  }
  const fields: TemplateColumn[] = [];
  const labels = new Set(templateColumns.map(c => c.label.toLowerCase()));
  for (const [key, values] of seen) {
    if (taken.has(key) || values.every(isEmpty)) continue;
    // Two fields with the same name (the template's "Open now" and the reader's openNow) are told apart by key.
    let label = humanize(key);
    if (labels.has(label.toLowerCase())) label = `${label} (${key})`;
    labels.add(label.toLowerCase());
    // Deeper objects inside a nested field are shown as one cell (their name or JSON).
    fields.push({ key, label, format: guessFormat(key, values) });
    if (fields.length >= MAX_FIELDS) break;
  }
  fields.sort((a, b) => a.label.localeCompare(b.label));
  // The page each row came from (kept internal as _sourceUrl): useful when one run reads many pages.
  if (!taken.has("_sourceUrl") && rows.some(r => typeof r._sourceUrl === "string")) fields.unshift({ key: "_sourceUrl", label: "Source page", format: "url" });
  return fields;
}
